import { useEffect, useMemo, useState } from "react";
import { DateTime, Duration } from "luxon";
import { Asterisk, Card, Colors, fontMedium, fontSmall, Icon, TimeInput } from "swing-components";

import { Toggle, useAuth } from "~components";
import { translate } from "~utils";

import {
  PunchType,
  type Paycode,
  type Punch,
  type Timecard,
} from "../../TimesheetEntry/TimesheetEntry";

export type TimeInterval = {
  startTime?: DateTime;
  endTime?: DateTime;
};

type TimecardEntryFormProps = {
  /**
   * Type of the timesheet entry.
   * - "Timecard": supports meal breaks and prefills times if available.
   * - "Paycode": no meal breaks allowed; used for one-off entries.
   * @default "Timecard"
   */
  entryType?: "Timecard" | "Paycode";
  paycodeDurationInMinutes?: number;
  timecardPunches?: Array<Punch>;

  onChange: (update: Partial<Timecard> | Partial<Paycode>) => void;
};

export function TimecardEntryForm(props: TimecardEntryFormProps) {
  const { entryType = "Timecard", paycodeDurationInMinutes, timecardPunches, onChange } = props;

  /********* State ********/
  const [mealBreakVisibility, setMealBreakVisibility] = useState<Record<number, boolean>>({});
  const [hasUserToggledDefault, setHasUserToggledDefault] = useState(false);
  const [clearedBreaks, setClearedBreaks] = useState<Record<number, boolean>>({});
  // Counter for generating temporary IDs
  const [tempIdCounter, setTempIdCounter] = useState(0);
  // Local state for punches with temporary IDs
  const [localPunches, setLocalPunches] = useState<Punch[]>(timecardPunches ?? []);

  // Duration state for paycode entries
  const [durationHours, setDurationHours] = useState<number>(() => {
    if (entryType === "Paycode" && paycodeDurationInMinutes !== undefined) {
      return Math.floor(paycodeDurationInMinutes / 60);
    }
    return 0;
  });
  const [durationMinutes, setDurationMinutes] = useState<number>(() => {
    if (entryType === "Paycode" && paycodeDurationInMinutes !== undefined) {
      return paycodeDurationInMinutes % 60;
    }
    return 0;
  });

  // Update local punches when props change
  useEffect(() => {
    setLocalPunches(timecardPunches ?? []);
  }, [timecardPunches]);

  let punchIn: Punch | undefined;
  let punchOut: Punch | undefined;

  if (entryType === "Timecard") {
    // Sort punches by timestamp to ensure correct order
    // TODO: Check if this is needed
    const sortedPunches = [...localPunches].sort((a, b) => {
      const timeA = DateTime.fromISO(a.ts);
      const timeB = DateTime.fromISO(b.ts);
      return timeA.toMillis() - timeB.toMillis();
    });

    // Work start time: First PUNCH_IN
    punchIn = sortedPunches.find((p) => p.type === PunchType.PunchIn);
    // Work end time: Last PUNCH_OUT
    punchOut = sortedPunches.reverse().find((p) => p.type === PunchType.PunchOut);
  }

  const initialTimeInterval: TimeInterval = useMemo(
    () => ({
      startTime: punchIn ? DateTime.fromISO(punchIn.ts) : undefined,
      endTime: punchOut ? DateTime.fromISO(punchOut.ts) : undefined,
    }),
    [punchIn, punchOut],
  );

  /**
   * TEMPORARY ID SYSTEM FOR MEAL BREAK TRACKING
   *
   * To help track meal break pairing locally, we generate temporary IDs:
   *
   * 1. All new punches (PunchIn, PunchOut, BreakOut, BreakIn) get temporary IDs if they don't have API IDs
   * 2. Temporary IDs start with "temp_" prefix to distinguish them from API IDs
   * 3. These IDs help track which punches belong to which meal break pairs locally
   * 4. Before sending to the API via onChange, all temporary IDs are stripped
   * 5. Only API-provided IDs (non-temp) are preserved when submitting
   *
   */

  /**
   * Generates a temporary ID for local tracking
   * Temporary IDs start with "temp_" to distinguish them from API IDs
   */
  function generateTempId(): string {
    const newId = `temp_${Date.now()}_${tempIdCounter}`;
    setTempIdCounter((prev) => prev + 1);
    return newId;
  }

  /** Calculates required meal breaks based on work period duration: 1 meal break per 5 hours */
  const requiredMealBreaks = useMemo(() => {
    const { startTime, endTime } = initialTimeInterval ?? {};
    if (!startTime || !endTime) return 0;
    const hours = endTime.diff(startTime).as("hours");
    return Math.floor(hours / 5);
  }, [initialTimeInterval]);

  const breakPairs = useMemo(() => getBreakPairs(localPunches), [localPunches]);

  /**
   * Returns a list of rendered meal break objects, each representing a break input row.
   * - Ensures at least one row is rendered for "Timecard" entries, even if worked hours are < 5.
   * - Includes actual punch timestamps (breakOut/startTime, breakIn/endTime) if present.
   * - Visibility is true if any punch exists; otherwise toggle starts off.
   */
  const renderedMealBreaks = useMemo(() => {
    const breaks: (TimeInterval & { isVisible?: boolean })[] = [];

    const minBreaks = Math.max(1, requiredMealBreaks);

    const updatedVisibility = { ...mealBreakVisibility };

    for (let i = 0; i < minBreaks; i++) {
      const pair = breakPairs[i];

      // Initialize default visibility only once
      if (updatedVisibility[i] === undefined) {
        const hasAnyPunch = !!(pair?.breakOut || pair?.breakIn);
        const isRequired = i < requiredMealBreaks;

        // Default row (index 0, <5 hours) will be rendered but toggle is OFF
        updatedVisibility[i] = hasAnyPunch || isRequired;
      }

      const isVisible = updatedVisibility[i];

      breaks.push({
        startTime: isVisible && pair?.breakOut ? DateTime.fromISO(pair.breakOut.ts) : undefined,
        endTime: isVisible && pair?.breakIn ? DateTime.fromISO(pair.breakIn.ts) : undefined,
        isVisible,
      });
    }

    // Save any initialized visibility values back into state
    if (Object.keys(updatedVisibility).length !== Object.keys(mealBreakVisibility).length) {
      setMealBreakVisibility(updatedVisibility);
    }

    return breaks;
  }, [breakPairs, requiredMealBreaks, mealBreakVisibility]);

  /**
   * Wrapper for onChange that strips temporary IDs before calling the original onChange
   */
  function handleChange(update: Partial<Timecard> | Partial<Paycode>) {
    if ("punches" in update && update.punches) {
      // Update local state with punches that include temporary IDs
      setLocalPunches(update.punches);

      // Strip temporary IDs before sending to API
      const strippedPunches = stripTempIds(update.punches);
      onChange({ ...update, punches: strippedPunches });
    } else {
      onChange(update);
    }
  }

  /**
   * Updates local punches state and calls onChange with stripped IDs
   */
  function updateLocalPunches(newPunches: Punch[]) {
    setLocalPunches(newPunches);
    const strippedPunches = stripTempIds(newPunches);
    onChange({ punches: strippedPunches });
  }

  /**
   * Toggles the visibility of a meal break
   */
  function toggleMealBreakVisibility({
    index,
    punches,
    setMealBreakVisibility,
  }: {
    index: number;
    punches: Punch[];
    setMealBreakVisibility: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  }) {
    setMealBreakVisibility((prev) => {
      const newState = { ...prev };
      const isTogglingOff = newState[index];

      if (isTogglingOff) {
        const breakPairs = getBreakPairs(punches);
        const pair = breakPairs[index];

        const updatedPunches = punches.filter((p) => p !== pair?.breakOut && p !== pair?.breakIn);
        updateLocalPunches(updatedPunches);

        // Mark break as cleared
        setClearedBreaks((prev) => ({ ...prev, [index]: true }));
      } else {
        // Clear the cleared state when toggling back on
        setClearedBreaks((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }

      newState[index] = !newState[index];
      return newState;
    });
  }

  /********* Handlers ********/
  function handleStartTimeChange(dateTime: DateTime) {
    const timeStamp = dateTime.toUTC().toISO();

    const newPunch: Punch = {
      type: PunchType.PunchIn,
      ts: timeStamp,
      // Add temporary ID for local tracking if no existing ID
      id: punchIn?.id || generateTempId(),
    };

    let updatedPunches = addOrUpdatePunchById({ punches: localPunches, newPunch });

    // Sort punches chronologically in ascending order
    updatedPunches = sortPunches(updatedPunches);
    updateLocalPunches(updatedPunches);
  }

  function handleDurationHoursChange(hours: number) {
    const validHours = Math.max(0, Math.min(24, Math.floor(hours)));
    setDurationHours(validHours);
    const totalMinutes = validHours * 60 + durationMinutes;
    handleChange({ durationInMinutes: totalMinutes });
  }

  function handleDurationMinutesChange(minutes: number) {
    const validMinutes = Math.max(0, Math.min(59, Math.floor(minutes)));
    setDurationMinutes(validMinutes);
    const totalMinutes = durationHours * 60 + validMinutes;
    handleChange({ durationInMinutes: totalMinutes });
  }

  function handleEndTimeChange(dateTime: DateTime) {
    const timeStamp = dateTime.toUTC().toISO();

    const newPunch: Punch = {
      type: PunchType.PunchOut,
      ts: timeStamp,
      // Add temporary ID for local tracking if no existing ID
      id: punchOut?.id || generateTempId(),
    };

    let updatedPunches = addOrUpdatePunchById({ punches: localPunches, newPunch });

    // Sort punches chronologically in ascending order
    updatedPunches = sortPunches(updatedPunches);
    updateLocalPunches(updatedPunches);
  }

  function handleMealBreakChange(index: number, updates: Partial<TimeInterval>) {
    const punches = localPunches;

    const allPairs = getBreakPairs(punches);
    const pair = allPairs[index];

    let updatedPunches = [...punches];

    // Replace breakOut (meal break start)
    if (updates.startTime) {
      const breakOutTS = updates.startTime.toUTC().toISO();
      const existingBreakOut = pair?.breakOut;
      const newBreakOut: Punch = {
        type: PunchType.PunchOut,
        ts: breakOutTS,
        // Preserve existing ID or generate temporary one
        id: existingBreakOut?.id || generateTempId(),
      };

      updatedPunches = replaceOrAddPunch(updatedPunches, existingBreakOut, newBreakOut);
    }

    // Replace breakIn (meal break end)
    if (updates.endTime) {
      const breakInTS = updates.endTime.toUTC().toISO();
      const existingBreakIn = pair?.breakIn;
      const newBreakIn: Punch = {
        type: PunchType.PunchIn,
        ts: breakInTS,
        // Preserve existing ID or generate temporary one
        id: existingBreakIn?.id || generateTempId(),
      };

      updatedPunches = replaceOrAddPunch(updatedPunches, existingBreakIn, newBreakIn);
    }

    updatedPunches = sortPunches(updatedPunches);
    updateLocalPunches(updatedPunches);
  }

  /********* Render ********/
  return (
    <Card>
      <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Time */}
        <div>
          {/* Top Row */}
          <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="Clock" />
            <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
              {translate("TIMECARD_ENTRY_FORM_TIME_LABEL")}
              <Asterisk />
            </p>
          </div>

          {/* Main Time Inputs */}
          <div>
            {entryType === "Paycode" ? (
              // Duration inputs for paycode entries
              <>
                {/* Hours */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0 }}>Hours</p>
                  <input
                    type="number"
                    value={durationHours.toString()}
                    onChange={(e) => handleDurationHoursChange(parseInt(e.target.value) || 0)}
                    min="0"
                    max="24"
                    css={{
                      width: 90,
                      textAlign: "center",
                      padding: "8px",
                      border: `1px solid ${Colors.slate300}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                {/* Minutes */}
                <div
                  css={{
                    padding: "16px 0",
                    borderTop: `1px solid ${Colors.slate200}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0 }}>Minutes</p>
                  <input
                    type="number"
                    value={durationMinutes.toString()}
                    onChange={(e) => handleDurationMinutesChange(parseInt(e.target.value) || 0)}
                    min="0"
                    max="59"
                    css={{
                      width: 90,
                      textAlign: "center",
                      padding: "8px",
                      border: `1px solid ${Colors.slate300}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </>
            ) : (
              // Time inputs for timecard entries
              <>
                {/* Start Time */}
                <div
                  css={{
                    padding: "16px 0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0 }}>{translate("TIMECARD_ENTRY_FORM_START_TIME")}</p>
                  <TimeInputOrPlaceholder
                    value={initialTimeInterval.startTime}
                    onChange={handleStartTimeChange}
                    getPrefillTime={() =>
                      getPunchPrefillTime({ punches: localPunches, type: PunchType.PunchIn })
                    }
                  />
                </div>
                {/* End Time */}
                <div
                  css={{
                    padding: "16px 0",
                    borderTop: `1px solid ${Colors.slate200}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <p css={{ margin: 0 }}>{translate("TIMECARD_ENTRY_FORM_END_TIME")}</p>
                  <TimeInputOrPlaceholder
                    value={initialTimeInterval.endTime}
                    onChange={handleEndTimeChange}
                    getPrefillTime={() =>
                      getPunchPrefillTime({ punches: localPunches, type: PunchType.PunchOut })
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Meal Breaks */}
        {entryType === "Timecard" && renderedMealBreaks.length > 0 && (
          <>
            <Divider />
            <div>
              {renderedMealBreaks.map((breakPeriod, index) => {
                const pair = breakPairs[index];

                // Only disable toggle if there are punches that came from the API (non-temporary IDs)
                const hasApiPunch = !!(
                  (pair?.breakOut && !pair.breakOut.id?.startsWith("temp_")) ||
                  (pair?.breakIn && !pair.breakIn.id?.startsWith("temp_"))
                );

                // If sub worked < 5 hours, the first row is our "default" suggestion
                const isDefault = requiredMealBreaks === 0 && index === 0;
                // toggle is off, inputs are display but disabled
                const defaultLocked = isDefault && !breakPeriod.isVisible && !hasUserToggledDefault;

                return (
                  <div key={index}>
                    {/* Header */}
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Icon name="Food" />
                        <p css={{ margin: 0, ...fontMedium({ fontWeight: 600 }) }}>
                          {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_LABEL", {
                            count: renderedMealBreaks.length > 1 ? index + 1 : undefined,
                          })}
                        </p>
                      </div>
                      <Toggle
                        isSelected={breakPeriod.isVisible}
                        // Disable the switch only when there are API punches (non-temporary IDs)
                        disabled={hasApiPunch}
                        onToggleChange={() => {
                          if (hasApiPunch) return;

                          // Track that the user has interacted with the default break toggle
                          if (requiredMealBreaks === 0 && index === 0) {
                            setHasUserToggledDefault(true);
                          }

                          toggleMealBreakVisibility({
                            index,
                            punches: localPunches,
                            setMealBreakVisibility,
                          });
                        }}
                      />
                    </div>

                    <div
                      css={{
                        overflow: "hidden",
                        transition: "max-height 0.3s ease-in-out",
                        // Always expand the first break
                        // Even if work period is under 5 hours we want
                        // to show the option of adding a first break

                        maxHeight:
                          breakPeriod.isVisible ||
                          (requiredMealBreaks === 0 && index === 0 && !hasUserToggledDefault)
                            ? "200px"
                            : "0px",
                      }}
                    >
                      {/* Break In */}
                      <div
                        css={{
                          padding: "8px 0",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <p css={{ margin: 0, color: defaultLocked ? Colors.slate400 : undefined }}>
                          {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_IN")}
                        </p>
                        <TimeInputOrPlaceholder
                          value={breakPeriod.startTime}
                          onChange={(newTime) =>
                            handleMealBreakChange(index, { startTime: newTime })
                          }
                          getPrefillTime={() =>
                            getMealBreakPrefillTime({
                              index,
                              punches: localPunches,
                              punchInISO: localPunches.find((p) => p.type === PunchType.PunchIn)
                                ?.ts,
                              punchOutISO: localPunches.find((p) => p.type === PunchType.PunchOut)
                                ?.ts,
                              fieldBeingEdited: "start",
                            })
                          }
                          isDisabled={defaultLocked}
                          wasCleared={clearedBreaks[index]}
                        />
                      </div>

                      {/* Break Out */}
                      <div
                        css={{
                          padding: "8px 0",
                          borderTop: `1px solid ${Colors.slate200}`,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <p
                          css={{
                            margin: 0,
                            color: defaultLocked ? Colors.slate400 : undefined,
                          }}
                        >
                          {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_OUT")}
                        </p>
                        <TimeInputOrPlaceholder
                          value={breakPeriod.endTime}
                          onChange={(newTime) => handleMealBreakChange(index, { endTime: newTime })}
                          getPrefillTime={() =>
                            getMealBreakPrefillTime({
                              index,
                              punches: localPunches,
                              punchInISO: localPunches.find((p) => p.type === PunchType.PunchIn)
                                ?.ts,
                              punchOutISO: localPunches.find((p) => p.type === PunchType.PunchOut)
                                ?.ts,
                              fieldBeingEdited: "end",
                            })
                          }
                          isDisabled={defaultLocked}
                          wasCleared={clearedBreaks[index]}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {requiredMealBreaks > 0 && (
              <MealBreakHint
                timeInterval={initialTimeInterval}
                requiredMealBreaks={requiredMealBreaks}
                mealBreaks={renderedMealBreaks}
              />
            )}
          </>
        )}
        <Divider />

        {/* Total Hours */}
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            ...fontMedium({ fontWeight: 600 }),
          }}
        >
          <p css={{ margin: 0 }}>{translate("LABEL_TOTAL_HOURS")}</p>
          <div css={{ minWidth: 96, textAlign: "center" }}>
            {(() => {
              if (entryType === "Paycode") {
                // For paycode entries, calculate from duration hours and minutes
                const totalMinutes = durationHours * 60 + durationMinutes;
                if (totalMinutes === 0) return "--";

                const duration = Duration.fromMillis(totalMinutes * 60 * 1000);
                return translate("TIMECARD_ENTRY_FORM_TOTAL_HOURS", {
                  duration: formatDuration(duration),
                });
              } else {
                // For timecard entries, use existing logic
                const { startTime, endTime } = initialTimeInterval;
                if (!startTime || !endTime) return "--";

                const duration = totalWorkedDuration(initialTimeInterval, renderedMealBreaks);
                const hours = duration.as("hours");

                return hours > 0
                  ? translate("TIMECARD_ENTRY_FORM_TOTAL_HOURS", {
                      duration: formatDuration(duration),
                    })
                  : "--";
              }
            })()}
          </div>
        </div>
      </div>
    </Card>
  );
}

/******* Helper Components *******/
function Divider() {
  return <div css={{ width: "100%", height: 1, backgroundColor: Colors.slate300 }} />;
}

type MealBreakHintProps = {
  timeInterval?: TimeInterval;
  requiredMealBreaks: number;
  mealBreaks: (TimeInterval & { isVisible?: boolean })[];
};

export function MealBreakHint(props: MealBreakHintProps) {
  const { timeInterval, requiredMealBreaks, mealBreaks } = props;
  const { startTime, endTime } = timeInterval ?? {};
  if (!startTime || !endTime) return null;

  // Calculate total worked hours
  const workHours = endTime.diff(startTime).as("hours");

  // Calculate total minutes of all logged meal breaks
  const totalBreakMinutes = mealBreaks.reduce((sum, b) => {
    if (b.startTime && b.endTime) {
      return sum + b.endTime.diff(b.startTime, "minutes").minutes;
    }
    return sum;
  }, 0);

  // Determine how many minutes of meal breaks are expected
  // 1 break (30 min) per 5 hours → 30 * requiredMealBreaks
  const expectedBreakMinutes = requiredMealBreaks * 30;

  // If 10+ hours worked and not enough break minutes logged,
  // show the required meal break message
  if (requiredMealBreaks >= 2 && totalBreakMinutes < expectedBreakMinutes) {
    return (
      <div css={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <Icon name="Alert" color={Colors.yellow600} />
        <p css={{ ...fontSmall(), margin: 0 }}>
          {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_HINT_REQUIRED", {
            hours: Math.floor(workHours),
            count: requiredMealBreaks,
          })}
        </p>
      </div>
    );
  }

  // If between 5 and <10 hours worked, and less than 30 minutes break logged,
  // show a reminder that a break is encouraged
  if (
    requiredMealBreaks === 1 &&
    totalBreakMinutes < expectedBreakMinutes &&
    mealBreaks.some((b) => b.startTime && b.endTime && b.isVisible !== false)
  ) {
    return (
      <div css={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
        <Icon name="Alert" color={Colors.yellow600} />
        <p css={{ ...fontSmall(), margin: 0 }}>
          {translate("TIMECARD_ENTRY_FORM_MEAL_BREAK_HINT_REMINDER")}
        </p>
      </div>
    );
  }

  return null;
}

type TimeInputOrPlaceholderProps = {
  value?: DateTime;
  onChange: (newTime: DateTime) => void;
  /**
   * Optional function to get a prefill time.
   * If not provided, the input will fallback to now.
   */
  getPrefillTime?: () => DateTime;
  isDisabled?: boolean;
  wasCleared?: boolean;
};

function TimeInputOrPlaceholder(props: TimeInputOrPlaceholderProps) {
  const { value, onChange, getPrefillTime, isDisabled, wasCleared } = props;

  const {
    userInfo: { address },
  } = useAuth();

  const isCalifornia = address?.state === "CA";

  // Input is initially shown only if:
  // - user is NOT in California
  // - there is already a value provided
  const [showInput, setShowInput] = useState(!isCalifornia && !!value);

  useEffect(() => {
    if (!value) {
      setShowInput(false); // hide if value is missing
    } else {
      setShowInput(!isCalifornia); // only show if not CA
    }
  }, [isCalifornia, value]);

  useEffect(() => {
    if (wasCleared) {
      setShowInput(false);
    }
  }, [wasCleared]);

  /**
   * Handles when the user clicks the "Enter Time" button.
   * - Reveals the <TimeInput />
   * - Prefills a time using `getPrefillTime`, or falls back to now
   */
  function handleClickToEnterTime() {
    setShowInput(true);

    // Only prefill if not cleared
    if (!value && !wasCleared) {
      const prefillTime = getPrefillTime?.() ?? DateTime.now().startOf("minute");
      onChange(prefillTime);
    }
  }

  return (
    <div css={{ position: "relative", width: "fit-content" }}>
      {showInput ? (
        // Show the time input with current value or fallback to now
        <TimeInput
          value={
            value ??
            getPrefillTime?.() ?? // use prefill function
            DateTime.now().startOf("minute")
          }
          onChange={onChange}
          isDisabled={isDisabled}
        />
      ) : (
        // Show a placeholder "Enter Time" button that triggers prefill
        <div
          onClick={isDisabled ? undefined : handleClickToEnterTime}
          css={{
            backgroundColor: Colors.slate200,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            maxWidth: 90,
            lineHeight: "24px",
            padding: "0 8px",
            whiteSpace: "nowrap",
            color: isDisabled ? Colors.slate400 : undefined,
          }}
        >
          {translate("TIMECARD_ENTRY_FORM_TIME_INPUT_ENTER_TIME")}
        </div>
      )}
    </div>
  );
}

/******* Helper Functions *******/
/**
 * Calculates the total worked duration, subtracting any meal breaks.
 * Returns Duration of 0 if punchIn or punchOut is missing.
 */
export function totalWorkedDuration(
  workPeriod: TimeInterval,
  mealBreaks: (TimeInterval & { isVisible?: boolean })[] = [],
): Duration {
  if (!workPeriod.startTime || !workPeriod.endTime) return Duration.fromMillis(0);

  let total = workPeriod.endTime.diff(workPeriod.startTime);

  mealBreaks.forEach((b) => {
    if (b.startTime && b.endTime && b.isVisible !== false) {
      total = total.minus(b.endTime.diff(b.startTime));
    }
  });

  return total;
}

/**
 * Converts a Luxon Duration into a string representing decimal hours.
 * - Whole hours are trimmed (e.g., "6")
 * - Partial hours are shown with up to 2 decimal places (e.g., "6.25")
 */
export function formatDuration(duration: Duration) {
  const hours = duration.as("hours");
  return parseFloat(hours.toFixed(2)).toString();
}

type AddOrUpdatePunchByIdParams = {
  punches: Punch[];
  newPunch: Punch;
};

/********* Helper Functions ********/
function addOrUpdatePunchById(params: AddOrUpdatePunchByIdParams) {
  const { punches, newPunch } = params;

  // Try updating by ID if it exists
  if (newPunch.id) {
    const index = punches.findIndex((p) => p.id === newPunch.id);
    if (index !== -1) {
      const updated = [...punches];
      updated[index] = newPunch;
      return updated;
    }
  }

  // If no ID match, try updating by type + timestamp for unique types like PunchIn/Out
  const isUniqueType = newPunch.type === PunchType.PunchIn || newPunch.type === PunchType.PunchOut;
  if (isUniqueType) {
    const index = punches.findIndex((p) => p.type === newPunch.type);
    if (index !== -1) {
      const updated = [...punches];
      updated[index] = newPunch;
      return updated;
    }
  }

  // Otherwise, just append
  return [...punches, newPunch];
}

/**
 * Strips temporary IDs from punches before sending to API
 * Only keeps IDs that came from the original API (don't start with "temp_")
 */
function stripTempIds(punches: Punch[]): Punch[] {
  const stripped = punches.map((punch) => {
    const { id, ...rest } = punch;
    // Only keep the ID if it's not a temporary ID
    const result = id && !id.startsWith("temp_") ? { ...rest, id } : rest;
    return result;
  });
  return stripped;
}

/**
 * Calculates how many 30-minute meal breaks are required based on total work duration.
 * Assumes 1 break is required for every `breakThresholdHours` worked.
 *
 * Example:
 *  - 1 break per 5 hours → 5.01 to 10 hours → 1 break
 *  - >10 hours → 2 breaks
 *
 * @param workPeriod - The time interval representing the shift (start and end)
 * @param breakThresholdHours - How many hours must be worked before each required break
 * @returns number of required breaks
 */
export function getRequiredMealBreaks(
  workPeriod: TimeInterval,
  breakThresholdHours: number = 5,
): number {
  const { startTime, endTime } = workPeriod;

  if (!startTime || !endTime) return 0;

  const totalWorkedHours = endTime.diff(startTime, "hours").hours;

  // breaks are only added once the threshold is exceeded
  return Math.floor(totalWorkedHours / breakThresholdHours);
}

/**
 * Groups Punches into meal break pairs.
 * A meal break consists of a PUNCH_OUT (break start) followed by a PUNCH_IN (break end).
 * This function extracts the middle punch pairs, excluding the work start/end punches.
 *
 * Expected sequence: PUNCH_IN (work start), PUNCH_OUT (break start), PUNCH_IN (break end), PUNCH_OUT (work end)
 *
 * @param punches - array of all punch entries (assumed sorted chronologically by timestamp)
 * @returns array of break pairs: { breakOut?: Punch; breakIn?: Punch }
 */
function getBreakPairs(punches: Punch[]): Array<{ breakOut?: Punch; breakIn?: Punch }> {
  // Filter to only PunchIn and PunchOut types (punches assumed to be pre-sorted)
  const punchInOuts = punches.filter(
    (p) => p.type === PunchType.PunchIn || p.type === PunchType.PunchOut,
  );

  // For meal breaks, we need at least 4 punches: work start, break start, break end, work end
  if (punchInOuts.length < 4) {
    return [];
  }

  // Extract meal break pairs from the middle punches
  // Skip the first punch (work start) and last punch (work end)
  const middlePunches = punchInOuts.slice(1, -1);

  const pairs: Array<{ breakOut?: Punch; breakIn?: Punch }> = [];

  // Group consecutive PUNCH_OUT and PUNCH_IN pairs as meal breaks
  for (let i = 0; i < middlePunches.length; i += 2) {
    const breakOut = middlePunches[i]; // Should be PUNCH_OUT (break start)
    const breakIn = middlePunches[i + 1]; // Should be PUNCH_IN (break end)

    if (
      breakOut &&
      breakIn &&
      breakOut.type === PunchType.PunchOut &&
      breakIn.type === PunchType.PunchIn
    ) {
      // Found a complete meal break pair
      pairs.push({ breakOut, breakIn });
    } else if (breakOut && breakOut.type === PunchType.PunchOut) {
      // Found a break start without a break end
      pairs.push({ breakOut });
    } else if (breakIn && breakIn.type === PunchType.PunchIn) {
      // Found a break end without a break start (shouldn't happen in normal flow)
      pairs.push({ breakIn });
    }
  }

  return pairs;
}

function sortPunches(punches: Punch[]): Punch[] {
  return punches
    .slice()
    .sort((a, b) => DateTime.fromISO(a.ts).toMillis() - DateTime.fromISO(b.ts).toMillis());
}

type GetPunchPrefillTimeParams = {
  punches: Punch[];
  type: (typeof PunchType)[keyof typeof PunchType];
};

/**
 * Returns the timestamp of the given punch type from the provided punches array.
 *
 * Assumes the punch exists — based on request start and end times.
 * If not found, falls back to current time.
 */
export function getPunchPrefillTime(params: GetPunchPrefillTimeParams): DateTime {
  const { punches, type } = params;

  const punch = punches.find((p) => p.type === type);

  if (!punch) {
    return DateTime.now().toUTC();
  }

  return DateTime.fromISO(punch.ts);
}

type GetMealBreakPrefillTimeParams = {
  index: number;
  punches: Punch[];
  punchInISO?: string;
  punchOutISO?: string;
  /**
   * Which timestamp field are we generating a prefill for:
   * - "start" → editing the start time of a meal break
   * - "end"   → editing the end time of a meal break
   */
  fieldBeingEdited: "start" | "end";
};

/**
 * Returns a suggested (prefill) timestamp for either the start or end of a meal break.
 *
 * Prefill logic follows this priority:
 *
 * 1. If the break pair is partially filled:
 *    - If BreakOut exists and editing the end time (BreakIn), suggest BreakOut + 30 minutes.
 *    - If BreakIn exists and editing the start time (BreakOut), suggest BreakIn - 30 minutes.
 *
 * 2. If no punches exist for this break:
 *    - Estimate based on PunchIn + (5 hours × break index).
 *    - Each break is spaced 5 hours apart from the start of the shift.
 *    - Add 30 minutes if prefill is for BreakIn (end).
 *    - Clamp prefill to not exceed PunchOut (if provided).
 *
 * 3. If PunchIn is not available, fallback to current time.
 */
export function getMealBreakPrefillTime(params: GetMealBreakPrefillTimeParams): DateTime {
  const { index, punchInISO, punchOutISO, fieldBeingEdited, punches } = params;
  const pair = getBreakPairs(punches)[index];

  // BreakOut exists and editing BreakIn → BreakOut + 30 min
  if (pair?.breakOut && !pair.breakIn && fieldBeingEdited === "end") {
    return DateTime.fromISO(pair.breakOut.ts).plus({ minutes: 30 });
  }

  // BreakIn exists and editing BreakOut → BreakIn - 30 min
  if (pair?.breakIn && !pair.breakOut && fieldBeingEdited === "start") {
    return DateTime.fromISO(pair.breakIn.ts).minus({ minutes: 30 });
  }

  // BreakIn exists and user is editing BreakIn again (e.g. BreakOut cleared)
  if (pair?.breakIn && fieldBeingEdited === "end") {
    return DateTime.fromISO(pair.breakIn.ts).minus({ minutes: 30 });
  }

  // Estimate from punchIn if no break punches exist yet
  if (punchInISO) {
    const punchIn = DateTime.fromISO(punchInISO);
    const estimatedStart = punchIn.plus({ hours: (index + 1) * 5 });
    const estimatedEnd = estimatedStart.plus({ minutes: 30 });

    const punchOut = punchOutISO ? DateTime.fromISO(punchOutISO) : undefined;

    if (punchOut) {
      if (fieldBeingEdited === "start") {
        const latestStart = punchOut.minus({ minutes: 30 });
        return estimatedStart > latestStart ? latestStart : estimatedStart;
      }

      if (fieldBeingEdited === "end") {
        return estimatedEnd > punchOut ? punchOut : estimatedEnd;
      }
    }

    return fieldBeingEdited === "start" ? estimatedStart : estimatedEnd;
  }

  // Fallback: use current time in UTC
  return DateTime.now().toUTC().startOf("minute");
}

function replaceOrAddPunch(
  punches: Punch[],
  oldPunch: Punch | undefined,
  newPunch: Punch,
): Punch[] {
  const updated = [...punches];

  if (oldPunch) {
    const index = punches.findIndex((p) =>
      oldPunch.id ? p.id === oldPunch.id : p.type === oldPunch.type && p.ts === oldPunch.ts,
    );
    if (index !== -1) {
      updated[index] = newPunch;
      return updated;
    }
  }

  updated.push(newPunch);
  return updated;
}
